"use client"

import { useEffect, useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { getTipOfTheDay, type Tip } from "@/data/tips"
import { Lightbulb, X } from "lucide-react"

const STORAGE_KEY = "tip-of-the-day-last-shown"

export function TipOfTheDayModal() {
    const [isOpen, setIsOpen] = useState(false)
    const [tip, setTip] = useState<Tip | null>(null)

    useEffect(() => {
        // Check if we should show the tip today
        const lastShown = localStorage.getItem(STORAGE_KEY)
        const today = new Date().toDateString()

        if (lastShown !== today) {
            // Show the tip and mark as shown for today
            setTip(getTipOfTheDay())
            setIsOpen(true)
            localStorage.setItem(STORAGE_KEY, today)
        }
    }, [])

    const handleClose = () => {
        setIsOpen(false)
    }

    if (!tip) return null

    const categoryColors = {
        study: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        "test-taking": "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
        motivation: "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400",
        strategy: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
        wellness: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400"
    }

    const categoryLabels = {
        study: "Study Tips",
        "test-taking": "Test Taking",
        motivation: "Motivation",
        strategy: "Strategy",
        wellness: "Wellness"
    }

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogContent className="sm:max-w-[500px] border-2">
                <button
                    onClick={handleClose}
                    className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
                >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                </button>

                <DialogHeader className="space-y-4">
                    <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 shadow-lg">
                            <Lightbulb className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1">
                            <DialogTitle className="text-2xl font-bold">Tip of the Day</DialogTitle>
                            <Badge className={`mt-1 ${categoryColors[tip.category]}`} variant="secondary">
                                {categoryLabels[tip.category]}
                            </Badge>
                        </div>
                    </div>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <div className="flex items-start gap-3">
                        {tip.icon && (
                            <span className="text-4xl" role="img" aria-label="tip icon">
                                {tip.icon}
                            </span>
                        )}
                        <div className="flex-1 space-y-2">
                            <h3 className="text-lg font-semibold text-foreground">{tip.title}</h3>
                            <p className="text-muted-foreground leading-relaxed">{tip.content}</p>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t">
                    <Button onClick={handleClose} className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                        Got it, thanks!
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    )
}
